import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Property } from '@/mocks/properties';

interface MapViewProps {
  properties: Property[];
  savedIds: string[];
  onSave: (id: string) => void;
}

const formatPrice = (price: number, type: string) => {
  if (type === 'rent') return `$${price.toLocaleString()}/mo`;
  return `$${(price / 1000).toFixed(0)}K`;
};

const MapView = ({ properties, savedIds, onSave }: MapViewProps) => {
  const [activeId, setActiveId] = useState<string | null>(
    properties.length > 0 ? properties[0].id : null
  );
  const navigate = useNavigate();

  const activeProperty = properties.find(p => p.id === activeId) ?? properties[0] ?? null;

  // Build Google Maps embed URL centered on the active property
  const mapSrc = activeProperty
    ? `https://www.google.com/maps/embed/v1/place?key=AIzaSyD-9tSrke72PouQMnMX-a7eZSW0jkFMBWY&q=${encodeURIComponent(activeProperty.location)}&zoom=14`
    : `https://www.google.com/maps/embed/v1/view?key=AIzaSyD-9tSrke72PouQMnMX-a7eZSW0jkFMBWY&center=40.7128,-74.0060&zoom=4`;

  return (
    <div className="flex gap-0 rounded-2xl overflow-hidden border border-stone-200 bg-white" style={{ height: '680px' }}>
      {/* Left: Scrollable property list */}
      <div className="w-full lg:w-[380px] flex-shrink-0 flex flex-col border-r border-stone-100 overflow-hidden">
        <div className="px-4 py-3 border-b border-stone-100 bg-stone-50 flex-shrink-0">
          <p className="text-xs text-stone-500 font-medium">
            <strong className="text-stone-800">{properties.length}</strong> properties on map
          </p>
        </div>
        <div className="overflow-y-auto flex-1">
          {properties.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center px-6">
              <div className="w-12 h-12 flex items-center justify-center bg-stone-100 rounded-full mb-3">
                <i className="ri-map-pin-line text-xl text-stone-400"></i>
              </div>
              <p className="text-stone-500 text-sm font-medium">No properties match your filters</p>
              <p className="text-stone-400 text-xs mt-1">Try adjusting your search criteria</p>
            </div>
          ) : (
            properties.map(property => {
              const isActive = property.id === activeId;
              const isSaved = savedIds.includes(property.id);
              return (
                <button
                  key={property.id}
                  onClick={() => setActiveId(property.id)}
                  className={`w-full text-left flex gap-3 p-3 border-b border-stone-50 transition-all cursor-pointer group ${
                    isActive ? 'bg-amber-50 border-l-2 border-l-amber-500' : 'hover:bg-stone-50 border-l-2 border-l-transparent'
                  }`}
                >
                  {/* Thumbnail */}
                  <div className="w-20 h-16 rounded-xl overflow-hidden flex-shrink-0 bg-stone-100">
                    <img
                      src={property.image}
                      alt={property.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className={`text-xs font-bold leading-snug line-clamp-2 mb-0.5 transition-colors ${isActive ? 'text-amber-700' : 'text-stone-800'}`}>
                      {property.title}
                    </p>
                    <p className="text-stone-400 text-xs flex items-center gap-0.5 mb-1.5 truncate">
                      <i className="ri-map-pin-line flex-shrink-0"></i>
                      <span className="truncate">{property.city}</span>
                    </p>
                    <div className="flex items-center justify-between">
                      <span className={`text-xs font-bold ${isActive ? 'text-amber-600' : 'text-stone-700'}`}>
                        {formatPrice(property.price, property.priceType)}
                      </span>
                      <div className="flex items-center gap-2 text-stone-400 text-xs">
                        <span className="flex items-center gap-0.5">
                          <i className="ri-hotel-bed-line"></i>{property.bedrooms}
                        </span>
                        <span className="flex items-center gap-0.5">
                          <i className="ri-drop-line"></i>{property.bathrooms}
                        </span>
                      </div>
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>
      </div>

      {/* Right: Map + active property overlay */}
      <div className="hidden lg:flex flex-1 flex-col relative overflow-hidden">
        {/* Google Maps Embed */}
        <iframe
          key={activeId}
          title="Property Map"
          src={mapSrc}
          className="w-full h-full border-0"
          allowFullScreen
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        />

        {/* Active property card overlay */}
        {activeProperty && (
          <div className="absolute bottom-4 left-4 right-4 bg-white rounded-2xl border border-stone-100 p-4 flex gap-4 items-center">
            <div className="w-16 h-14 rounded-xl overflow-hidden flex-shrink-0">
              <img
                src={activeProperty.image}
                alt={activeProperty.title}
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-stone-800 text-sm truncate">{activeProperty.title}</p>
              <p className="text-stone-400 text-xs flex items-center gap-1 mt-0.5 truncate">
                <i className="ri-map-pin-line text-amber-500"></i>
                {activeProperty.location}
              </p>
              <div className="flex items-center gap-3 mt-1.5 text-xs text-stone-500">
                <span className="flex items-center gap-1"><i className="ri-hotel-bed-line"></i>{activeProperty.bedrooms} Beds</span>
                <span className="flex items-center gap-1"><i className="ri-drop-line"></i>{activeProperty.bathrooms} Baths</span>
                <span className="flex items-center gap-1"><i className="ri-layout-line"></i>{activeProperty.sqft.toLocaleString()} sqft</span>
              </div>
            </div>
            <div className="flex flex-col items-end gap-2 flex-shrink-0">
              <span className="font-black text-amber-600 text-base">
                {formatPrice(activeProperty.price, activeProperty.priceType)}
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={e => { e.stopPropagation(); onSave(activeProperty.id); }}
                  className="w-8 h-8 flex items-center justify-center rounded-full border border-stone-200 hover:border-red-300 transition-colors cursor-pointer"
                >
                  <i className={`${savedIds.includes(activeProperty.id) ? 'ri-heart-fill text-red-500' : 'ri-heart-line text-stone-400'} text-sm`}></i>
                </button>
                <button
                  onClick={() => navigate(`/listings/${activeProperty.id}`)}
                  className="px-3 py-1.5 bg-amber-500 hover:bg-amber-400 text-white text-xs font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >
                  View Details
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Property count badge */}
        <div className="absolute top-4 right-4 bg-white/95 backdrop-blur-sm rounded-xl px-3 py-2 border border-stone-100 flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse"></div>
          <span className="text-xs font-semibold text-stone-700">{properties.length} properties</span>
        </div>
      </div>
    </div>
  );
};

export default MapView;
